import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import {
	IImpersonationContextComponentProps,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getIndustries, getIndustryName, isTemplateNew } from '../../../../../models/UiUtils';
import {
	useEmailCategoryTemplates,
	useEmailSearchTemplates,
	useMyTemplatesQuery,
	useTemplateIndustryCategoriesQuery,
} from '../../../../../queries';
import {
	ImpersonationBroker,
	Industry,
	ITemplate,
	ITemplateCard,
	KnownCategories,
	TemplateType,
} from '../../../../../viewmodels/AppViewModels';
import { emailActivityTintColor } from '../../../../styles/colors';
import { bs } from '../../../../styles/styles';
import { Button } from '../../../Button';
import { CampaignExpiryHint } from '../../../campaigns/CampaignExpiryHint';
import { CampaignSearchInputField } from '../../../campaigns/CampaignSearchInputField';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../HoverCard';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { asModalComponent } from '../../../Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../Select';
import { SocialMediaIndicatorIcon } from '../../../svgs/icons/SocialMediaIndicatorIcon';
import { CampaignSummaryFromContentShort } from '../../../templateCards/CampaignTemplateCard/presentation';
import { getTemplateApprovalStatus } from '../../../TemplateReviewStatusIndicator';
import { styleSheet } from './styles';

interface Props extends IImpersonationContextComponentProps, IModalContext<ITemplate> {
	title: React.ReactNode;
}

function _EmailTemplateBrowser(props: Props) {
	const { impersonationContext } = props;
	const id = React.useId();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('CampaignSearch');
	const account = impersonationContext?.isValid ? impersonationContext.account : userSession?.account;
	const [industry, setIndustry] = React.useState(account?.additionalInfo?.industry ?? Industry.Insurance);
	// my template enabled either when not impersonating or if csmViewMyCampaignsEnabled when impersonating
	const isMyTemplatesEnabled =
		impersonationContext == null || impersonationContext?.account?.preferences?.csmViewMyCampaignsEnabled;
	const [selectedCategory, setSelectedCategory] = React.useState(
		isMyTemplatesEnabled ? KnownCategories.MyTemplates : ''
	);
	const [searchQuery, setSearchQuery] = React.useState('');
	const debouncedSearchQuery = useDebounceValue(searchQuery);
	const templateIndustryCategoriesQuery = useTemplateIndustryCategoriesQuery({
		industry,
		impersonationContext,
	});
	const emailSearchTemplatesQuery = useEmailSearchTemplates({
		industry,
		query: debouncedSearchQuery,
		impersonationContext,
		enabled: Boolean(debouncedSearchQuery),
	});
	const myTemplatesQuery = useMyTemplatesQuery<ITemplateCard>({
		enabled: selectedCategory === KnownCategories.MyTemplates,
		industry,
		impersonationContext,
		templateType: TemplateType.Email,
	});
	const categoryTemplatesQuery = useEmailCategoryTemplates({
		enabled: Boolean(selectedCategory) && selectedCategory !== KnownCategories.MyTemplates,
		industry,
		impersonationContext,
		categoryName: selectedCategory,
	});
	const categories = React.useMemo(() => {
		let categoryList: string[] = isMyTemplatesEnabled ? [KnownCategories.MyTemplates] : [];
		if (templateIndustryCategoriesQuery.data) {
			categoryList = categoryList.concat(templateIndustryCategoriesQuery.data);
		}
		return categoryList;
	}, [templateIndustryCategoriesQuery.data, isMyTemplatesEnabled]);
	const onIndustryClicked = (option: Industry) => {
		setIndustry(option);
		setSelectedCategory(isMyTemplatesEnabled ? KnownCategories.MyTemplates : KnownCategories.Featured);
	};
	const onCategoryClicked = (option: string) => {
		setSelectedCategory(option);
	};
	const onCampaignTemplateCardClicked = async (card: ITemplateCard, socialIndicator?: boolean) => {
		const templateId = socialIndicator
			? card.associatedTemplates?.find(x => x.templateType === TemplateType.SocialMediaPost)?.id
			: card.id;
		if (templateId) {
			try {
				const template = await userSession.webServiceHelper.callAsync<ITemplate>(
					ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `template/${encodeURIComponent(templateId)}`,
						queryParams: {
							expand: 'LastUsedByDate',
						},
					}),
					'GET'
				);
				props.parentModal?.onRequestClose(template, !template);
			} catch (error) {
				errorMessages.pushApiError(error);
				logger.logApiError('TemplateLoad-Error', error);
			}
		}
	};
	const onCampaignSearch = (query: string) => {
		setSearchQuery(query);
	};
	let isLoading = false;
	if (debouncedSearchQuery && !emailSearchTemplatesQuery.isSuccess) {
		isLoading = true;
	} else if (selectedCategory === KnownCategories.MyTemplates && !myTemplatesQuery.isSuccess) {
		isLoading = true;
	} else if (selectedCategory !== KnownCategories.MyTemplates && !categoryTemplatesQuery.isSuccess) {
		isLoading = true;
	}
	const templates = React.useMemo(() => {
		let value: ITemplateCard[] = [];
		if (debouncedSearchQuery && emailSearchTemplatesQuery.data) {
			value = emailSearchTemplatesQuery.data;
		} else if (selectedCategory === KnownCategories.MyTemplates && myTemplatesQuery.data) {
			value = myTemplatesQuery.data;
		} else if (selectedCategory !== KnownCategories.MyTemplates && categoryTemplatesQuery.data) {
			value = categoryTemplatesQuery.data;
		}
		return value;
	}, [
		emailSearchTemplatesQuery.data,
		myTemplatesQuery.data,
		categoryTemplatesQuery.data,
		debouncedSearchQuery,
		selectedCategory,
	]);
	const industryOptions = getIndustries({
		accountId: impersonationContext?.account?.id ?? userSession?.account?.id,
	}).map(i => ({
		label: getIndustryName(i),
		value: i,
	}));
	return (
		<div className={`${css(styleSheet.container)}`}>
			<div className={css(styleSheet.header)}>
				{props.title || 'Create a new message template or pick from an existing one...'}
			</div>
			<div className={css(styleSheet.searchContainer)}>
				<CampaignSearchInputField onChange={onCampaignSearch} industry={industry} />
			</div>
			<Select items={industryOptions} value={industry} onValueChange={onIndustryClicked}>
				<SelectTrigger styles={styleSheet.trigger}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{industryOptions.map(i => (
						<SelectItem key={`${id}-${i.value}`} value={i.value}>
							{i.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{!debouncedSearchQuery ? (
				<Select value={selectedCategory} onValueChange={onCategoryClicked}>
					<SelectTrigger styles={styleSheet.trigger}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{categories
							.filter(category => category !== '')
							.map(category => (
								<SelectItem key={`campaign-search-category-option-${category}`} value={category}>
									{category}
								</SelectItem>
							))}
					</SelectContent>
				</Select>
			) : null}
			{isLoading ? <LoadingSpinner className={css(bs.absoluteCenter)} type='large' /> : null}
			{!isLoading ? (
				<div className={css(styleSheet.cardsContainer)}>
					{templates.map(templateCard => (
						<div
							key={templateCard.id}
							className={css(styleSheet.templateCard)}
							onClick={() => onCampaignTemplateCardClicked(templateCard)}
						>
							{getTemplateApprovalStatus(templateCard?.templateReview, false)}
							<div className={css(styleSheet.templateCardHeader)}>
								<div className={css(styleSheet.templateCardHeaderTitle)}>
									<span>{templateCard.name}</span>
									{templateCard?.schedule?.expirationDate ? (
										<CampaignExpiryHint expirationDate={moment(templateCard.schedule.expirationDate)} />
									) : null}
									{isTemplateNew(templateCard) ? <span style={{ color: emailActivityTintColor }}>(New!)</span> : null}
								</div>
								{!impersonationContext?.isValid &&
								userSession?.account?.features?.socialMedia?.enabled &&
								templateCard?.associatedTemplates?.length > 0 ? (
									<HoverCard>
										<HoverCardTrigger className={css(styleSheet.templateSocialIndicator)}>
											<SocialMediaIndicatorIcon className={css(bs.cursorPointer)} />
										</HoverCardTrigger>
										<HoverCardContent styles={[styleSheet.templateSocialPopover]}>
											Also available for social media
										</HoverCardContent>
									</HoverCard>
								) : null}
							</div>
							<CampaignSummaryFromContentShort card={templateCard} />
							<div className={css(styleSheet.templateCardButtonContainer)}>
								<Button
									kind='reverse'
									size='small'
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation();
										onCampaignTemplateCardClicked(templateCard);
									}}
									aria-label={`Select template ${templateCard.name}`}
								>
									Select Template
								</Button>
							</div>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
}

const EmailTemplateBrowserAsObserver = observer(_EmailTemplateBrowser);
export const EmailTemplateBrowser = inject(
	ModalChildComponentContextKey,
	ImpersonationContextKey
)(EmailTemplateBrowserAsObserver);

export const EmailTemplateBrowserDialog = asModalComponent(EmailTemplateBrowser, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
});
